import type { SVGProps } from 'react';

export type SectionIconName =
  | 'project' | 'design-method' | 'location-wind' | 'terrain' | 'buildings' | 'north'
  | 'basic' | 'roof' | 'pressure' | 'roof-shape' | 'insulation' | 'resistance'
  | 'catalogue' | 'substrate' | 'membrane' | 'fixing' | 'catalogue-status' | 'excluded'
  | 'accessories' | 'eta-products' | 'eta-status' | 'zone-layout' | 'layers'
  | 'calculation' | 'overall' | 'summary' | 'wind' | 'zones' | 'versions'
  | 'quantities' | 'warnings';

/** Maps visible section headings to the approved icon catalogue. */
export function sectionIconForTitle(title: string): SectionIconName | undefined {
  const value = title.toLowerCase().replace(/\s*\(\d+\)\s*$/, '').trim();
  if (value === 'input' || value === 'project') return 'project';
  if (value === 'result' || value === 'results' || value === 'overall status') return 'overall';
  if (value === 'design method') return 'design-method';
  if (value === 'location and wind') return 'location-wind';
  if (value === 'terrain and exposure') return 'terrain';
  if (value === 'nearby buildings') return 'buildings';
  if (value === 'north orientation') return 'north';
  if (value === 'basic parameters') return 'basic';
  if (value === 'roof parameters') return 'roof';
  if (value === 'internal pressure') return 'pressure';
  if (value === 'roof shape') return 'roof-shape';
  if (value === 'thermal insulation') return 'insulation';
  if (value === 'resistance basis' || value === 'governing resistance modes') return 'resistance';
  if (value === 'catalogue') return 'catalogue';
  if (value === 'substrate') return 'substrate';
  if (value === 'waterproof membrane' || value === 'waterproof membrane distribution') return 'membrane';
  if (value === 'fixing system') return 'fixing';
  if (value === 'catalogue status') return 'catalogue-status';
  if (value === 'excluded fixing systems') return 'excluded';
  if (value === 'accessories') return 'accessories';
  if (value === 'ejot eta products') return 'eta-products';
  if (value === 'eta source and engineering status') return 'eta-status';
  if (value === 'zone layout' || value.includes('zone f') || value.includes('zone g') || value.includes('zone h') || value.includes('zone i')) return 'zone-layout';
  if (value === 'layers' || value === 'view') return 'layers';
  if (value === 'calculation status') return 'calculation';
  if (value === 'engineering summary') return 'summary';
  if (value === 'wind parameters') return 'wind';
  if (value === 'zones') return 'zones';
  if (value === 'versions') return 'versions';
  if (value === 'summary' || value === 'quantities') return 'quantities';
  if (value === 'warnings') return 'warnings';
  return undefined;
}

export function SectionIcon({ name, ...props }: { name: SectionIconName } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <IconShape name={name} />
    </svg>
  );
}

function IconShape({ name }: { name: SectionIconName }) {
  switch (name) {
    case 'project':
      return <path d="M8 20h18l6 6h24v27H8zM8 20v-6h18l6 6" />;
    case 'design-method':
      return <><path d="M14 8h28l10 10v38H14zM42 8v12h12M22 28h16M22 36h11"/><path d="m22 46 6 6 13-14"/></>;
    case 'location-wind':
      return <><path d="M22 29c0-10 7-17 16-17s16 7 16 17c0 13-16 26-16 26S22 42 22 29z"/><circle cx="38" cy="29" r="5"/><path d="M5 18h12M5 26h10M4 34h13"/></>;
    case 'terrain':
      return <path d="m6 52 17-30 12 18 8-13 15 25zM17 52l12-20M42 43l7-10"/>;
    case 'buildings':
      return <path d="M8 54V25h18v29M30 54V14h24v40M14 33h6M14 41h6M37 23h9M37 32h9M37 41h9"/>;
    case 'north':
      return <><circle cx="32" cy="32" r="24"/><path d="M32 11v7M32 46v7M11 32h7M46 32h7m21-10-5 13-13 7 5-13z"/></>;
    case 'basic':
      return <><path d="M8 17h48M8 32h48M8 47h48"/><circle cx="23" cy="17" r="5"/><circle cx="42" cy="32" r="5"/><circle cx="29" cy="47" r="5"/></>;
    case 'roof':
      return <path d="M9 31 32 12l23 19v25H9zM18 56V38h28v18M9 8h34M13 8v6M23 8v4M33 8v6"/>;
    case 'pressure':
      return <path d="M10 48a23 23 0 1 1 44 0M32 48l12-18M18 43h5M41 21l3 5"/>;
    case 'roof-shape':
      return <><path d="m11 43 7-25 22-8 15 19-7 23-23 4z"/><circle cx="11" cy="43" r="2.6" fill="currentColor"/><circle cx="18" cy="18" r="2.6" fill="currentColor"/><circle cx="40" cy="10" r="2.6" fill="currentColor"/></>;
    case 'insulation':
      return <path d="m8 22 24-12 24 12-24 12zM8 32l24 12 24-12M8 42l24 12 24-12"/>;
    case 'resistance':
      return <path d="M32 7l20 8v15c0 13-8 22-20 28-12-6-20-15-20-28V15zm-9 25 6 6 13-15"/>;
    case 'catalogue':
      return <path d="M8 13h20c5 0 8 3 8 8v34c0-5-3-8-8-8H8zm48 0H44c-5 0-8 3-8 8v34c0-5 3-8 8-8h12z"/>;
    case 'substrate':
      return <><rect x="9" y="13" width="46" height="38" rx="2"/><path d="M24 13v38M39 13v38M9 26h46M9 39h46"/></>;
    case 'membrane':
      return <path d="M32 7c-6 11-18 22-18 33a18 18 0 0 0 36 0c0-11-12-22-18-33zM15 53h34M20 58h24"/>;
    case 'fixing':
      return <path d="M20 10h24M24 16h16M29 16v33l3 8 3-8V16M27 27h10M27 36h10M27 45h10"/>;
    case 'catalogue-status':
      return <><path d="M18 12h28v44H18zM25 12V7h14v5M24 43h16"/><path d="m24 28 5 5 10-11"/></>;
    case 'excluded':
      return <><circle cx="32" cy="32" r="23"/><path d="m16 16 32 32"/></>;
    case 'accessories':
      return <><rect x="8" y="23" width="48" height="30" rx="3"/><path d="M23 23v-8h18v8M8 34h48M28 31h8v7h-8z"/></>;
    case 'eta-products':
      return <><path d="M14 10h36v34H14zM22 20h20M22 28h14M25 44l-4 13 11-5 11 5-4-13"/><circle cx="32" cy="40" r="7"/></>;
    case 'eta-status':
      return <><path d="m14 37-4-4a10 10 0 0 1 0-14l8-8a10 10 0 0 1 14 0l3 3m15 13 4 4a10 10 0 0 1 0 14l-8 8a10 10 0 0 1-14 0l-3-3M23 41l18-18"/><path d="m38 45 5 5 11-13"/></>;
    case 'zone-layout':
      return <><rect x="8" y="10" width="48" height="44" rx="3"/><path d="M23 10v44M8 27h48M23 40h33"/></>;
    case 'layers':
      return <><path d="M7 32c8-13 17-19 25-19s17 6 25 19c-8 13-17 19-25 19S15 45 7 32z"/><circle cx="32" cy="32" r="8"/></>;
    case 'calculation':
      return <><rect x="14" y="8" width="36" height="48" rx="3"/><rect x="20" y="14" width="24" height="9"/><path d="M21 32h4M31 32h4M41 32h4M21 42h4M31 42h4M41 42h4"/></>;
    case 'overall':
      return <><circle cx="20" cy="21" r="7"/><circle cx="44" cy="43" r="7"/><path d="m18 49 28-34m-3 1 5 5 9-11"/></>;
    case 'summary':
      return <path d="M10 52h44M16 45V30h8v15M29 45V19h8v26M42 45V11h8v34"/>;
    case 'wind':
      return <path d="M7 20h33c9 0 9-10 2-10M7 32h44c8 0 8 10 1 10M7 44h25"/>;
    case 'zones':
      return <><rect x="8" y="10" width="48" height="44" rx="2"/><path d="M8 24h48M8 42h48M22 10v44M42 10v44"/></>;
    case 'versions':
      return <><circle cx="30" cy="32" r="22"/><path d="M30 18v15l11 7M47 12h9v9"/></>;
    case 'quantities':
      return <path d="m8 21 16-9 16 9-16 9zM24 30v18M8 21v18l16 9 16-9V21m-2 13 10-6 9 5-10 6M47 39v13"/>;
    case 'warnings':
      return <path d="M32 8 58 54H6zm0 15v15m0 8v1"/>;
  }
}
